import { RacePreset, SplitItem, SplitOptions, SplitStrategy, DistanceUnit, Language } from '../types';

export const KM_PER_MILE = 1.609344;
export const MILES_PER_KM = 1 / KM_PER_MILE;
export const FEET_PER_MILE = 5280;

export function kmToMiles(km: number): number {
  return km * MILES_PER_KM;
}

export function milesToKm(miles: number): number {
  return miles * KM_PER_MILE;
}

export function kmPaceToMilePace(secPerKm: number): number {
  return secPerKm * KM_PER_MILE;
}

export function milePaceToKmPace(secPerMile: number): number {
  return secPerMile / KM_PER_MILE;
}

export const RACE_PRESETS: RacePreset[] = [
  { id: '1k', name: '1 Kilometer', shortName: '1K', distanceKm: 1, category: 'sprint' },
  { id: '1m', name: '1 Engelse Mijl', shortName: '1M', distanceKm: 1.60934, category: 'sprint' },
  { id: '3k', name: '3 Kilometer', shortName: '3K', distanceKm: 3, category: 'sprint' },
  { id: '5k', name: '5 Kilometer', shortName: '5K', distanceKm: 5, category: 'standard' },
  { id: '5m', name: '5 Engelse Mijl', shortName: '5M', distanceKm: 8.04672, category: 'standard' },
  { id: '10k', name: '10 Kilometer', shortName: '10K', distanceKm: 10, category: 'standard' },
  { id: '15k', name: '15 Kilometer', shortName: '15K', distanceKm: 15, category: 'half' },
  { id: '10m', name: '10 Engelse Mijl', shortName: '10M', distanceKm: 16.0934, category: 'half' },
  { id: '21k', name: 'Halve Marathon', shortName: 'HMar', distanceKm: 21.0975, category: 'half' },
  { id: '30k', name: '30 Kilometer', shortName: '30K', distanceKm: 30, category: 'marathon' },
  { id: '42k', name: 'Hele Marathon', shortName: 'Mar', distanceKm: 42.195, category: 'marathon' },
  { id: '50k', name: '50K Ultramarathon', shortName: '50K', distanceKm: 50, category: 'ultra' },
];

/**
 * Format total seconds into HH:MM:SS or MM:SS
 */
export function formatTime(totalSeconds: number, forceHours: boolean = false): string {
  if (isNaN(totalSeconds) || totalSeconds < 0) return '00:00';
  const rounded = Math.round(totalSeconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const seconds = rounded % 60;

  const pad = (n: number) => n.toString().padStart(2, '0');

  if (hours > 0 || forceHours) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Format pace in seconds/km to MM:SS
 */
export function formatPace(paceSecondsPerKm: number): string {
  if (isNaN(paceSecondsPerKm) || paceSecondsPerKm <= 0) return '00:00';
  const rounded = Math.round(paceSecondsPerKm);
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Split seconds to { hours, minutes, seconds }
 */
export function secondsToHms(totalSeconds: number): { hours: number; minutes: number; seconds: number } {
  if (isNaN(totalSeconds) || totalSeconds < 0) return { hours: 0, minutes: 0, seconds: 0 };
  const rounded = Math.round(totalSeconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const seconds = rounded % 60;
  return { hours, minutes, seconds };
}

/**
 * Convert hours, minutes, seconds to total seconds
 */
export function hmsToSeconds(hours: number, minutes: number, seconds: number): number {
  return (Math.max(0, hours) * 3600) + (Math.max(0, minutes) * 60) + Math.max(0, seconds);
}

/**
 * Split pace seconds to { minutes, seconds }
 */
export function paceToMs(paceSecondsPerKm: number): { minutes: number; seconds: number } {
  if (isNaN(paceSecondsPerKm) || paceSecondsPerKm <= 0) return { minutes: 5, seconds: 0 };
  const rounded = Math.round(paceSecondsPerKm);
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  return { minutes, seconds };
}

/**
 * Calculate total time given distance in km and pace in sec/km
 */
export function calculateTime(distanceKm: number, paceSecsPerKm: number): number {
  if (distanceKm <= 0 || paceSecsPerKm <= 0) return 0;
  return Math.round(distanceKm * paceSecsPerKm);
}

/**
 * Calculate pace in sec/km given distance in km and total seconds
 */
export function calculatePace(distanceKm: number, totalSeconds: number): number {
  if (distanceKm <= 0 || totalSeconds <= 0) return 0;
  return totalSeconds / distanceKm;
}

/**
 * Calculate distance in km given pace in sec/km and total seconds
 */
export function calculateDistance(totalSeconds: number, paceSecsPerKm: number): number {
  if (paceSecsPerKm <= 0 || totalSeconds <= 0) return 0;
  return totalSeconds / paceSecsPerKm;
}

/**
 * Calculate speed in km/h given distance in km and total seconds
 */
export function calculateSpeed(distanceKm: number, totalSeconds: number): number {
  if (totalSeconds <= 0 || distanceKm <= 0) return 0;
  const hours = totalSeconds / 3600;
  return distanceKm / hours;
}

/**
 * Generate splits for a specified race distance and pace strategy
 */
export function generateSplits(
  distanceKm: number,
  targetPaceSecsPerKm: number,
  strategy: SplitStrategy = 'even',
  unit: DistanceUnit = 'km',
  options: SplitOptions = {}
): SplitItem[] {
  if (distanceKm <= 0 || targetPaceSecsPerKm <= 0) return [];

  const splits: SplitItem[] = [];
  const isMiles = unit === 'mi';
  const totalUnits = isMiles ? kmToMiles(distanceKm) : distanceKm;
  const wholeUnits = Math.floor(totalUnits);
  const partialUnit = totalUnits - wholeUnits;
  const effectivePaceSecs = isMiles ? kmPaceToMilePace(targetPaceSecsPerKm) : targetPaceSecsPerKm;

  const getPaceModifier = (index: number, total: number): number => {
    const progress = total > 1 ? index / (total - 1) : 0.5;
    switch (strategy) {
      case 'negative-2':
        return 1.01 - (progress * 0.02);
      case 'negative-5':
        return 1.025 - (progress * 0.05);
      case 'positive-3':
        return 0.985 + (progress * 0.03);
      case 'custom': {
        const spread = Math.min(15, Math.max(-15, options.customPct ?? 3)) / 100;
        return 1 + spread / 2 - progress * spread;
      }
      case 'even':
      default:
        return 1.0;
    }
  };

  const hasPartial = partialUnit > 0.02;
  const totalSplitCount = hasPartial ? wholeUnits + 1 : wholeUnits;
  // Een restje van <= 0.02 eenheid telt niet als eigen split, maar blijft wel meetellen in de totale afstand.
  const raceUnits = hasPartial ? totalUnits : wholeUnits + partialUnit;

  // Ruwe verdeling per split, daarna normaliseren zodat de som exact de doeltijd is.
  const lengths: number[] = [];
  for (let i = 0; i < wholeUnits; i++) lengths.push(1);
  if (hasPartial) lengths.push(partialUnit);
  if (lengths.length > 0 && !hasPartial) lengths[lengths.length - 1] += partialUnit;

  const modifiers = lengths.map((_, i) => getPaceModifier(i, totalSplitCount));
  if (options.slowStartPct && modifiers.length > 1) {
    modifiers[0] *= 1 + Math.min(15, Math.max(0, options.slowStartPct)) / 100;
  }
  const rawTotal = lengths.reduce((sum, len, i) => sum + len * modifiers[i], 0);
  const scale = rawTotal > 0 ? raceUnits / rawTotal : 1;

  let cumulativeSeconds = 0;
  lengths.forEach((len, i) => {
    const currentPaceSecs = effectivePaceSecs * modifiers[i] * scale;
    const splitSecs = currentPaceSecs * len;
    cumulativeSeconds += splitSecs;
    const isPartialSplit = hasPartial && i === lengths.length - 1;
    const label = isPartialSplit ? totalUnits.toFixed(2) : String(i + 1);

    splits.push({
      km: isPartialSplit ? Math.round(totalUnits * 100) / 100 : i + 1,
      splitNumber: i + 1,
      unitLabel: `${label} ${unit}`,
      splitSeconds: splitSecs,
      cumulativeSeconds,
      formattedSplit: formatPace(currentPaceSecs),
      formattedCumulative: formatTime(cumulativeSeconds),
      pacePerKm: currentPaceSecs,
    });
  });

  return splits;
}

/**
 * Format date nicely in Dutch or English
 */
export function formatDate(isoDate: string, language: Language = 'nl'): string {
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString(language === 'en' ? 'en-US' : 'nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

export const formatDutchDate = formatDate;
